/**
 * Labor time, typed the way people say it.
 *
 * Every labor field in the app reads hours by default — "2" is two hours, not
 * two minutes — because that is the unit operators quote work in. The unit is
 * only a default, though: "30m", "1h 30m", "90 min" and "1:30" all mean what
 * they look like, so nobody has to convert a half hour into 0.5 in their head.
 *
 * Storage is unchanged. The pricebook keeps labor as MINUTES
 * (`laborCostLines[].durationMinutes`) and as HOURS (`estimatedHours`), and
 * those two must never disagree, so both sides parse and render through here
 * rather than each rolling its own reader.
 */

export type DurationUnit = 'hours' | 'minutes';

const HOUR_UNIT = /^(?:h|hr|hrs|hour|hours)$/;
const MINUTE_UNIT = /^(?:m|min|mins|minute|minutes)$/;

/** "1:30" — an hours:minutes clock reading, never a bare number of minutes. */
const CLOCK = /^(\d+)\s*:\s*([0-5]?\d)$/;

export interface ParseDurationOptions {
  /**
   * What a bare number means. Hours everywhere in the product; the option
   * exists so a field that genuinely counts minutes can say so, not so callers
   * can pick a house style.
   */
  defaultUnit?: DurationUnit;
}

/**
 * Read typed text as a number of minutes, or `null` when it is not a duration
 * at all. An empty box reads as 0 — clearing a field is how you say "none".
 */
export function parseDurationToMinutes(
  raw: string,
  { defaultUnit = 'hours' }: ParseDurationOptions = {},
): number | null {
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return 0;

  const clock = normalized.match(CLOCK);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);

  // Walk the string with a sticky scanner so trailing junk fails the parse
  // instead of being silently ignored: "1h banana" is a typo, not 60 minutes.
  //
  // The leading zero is optional. ".5" is how a lot of people type half of
  // something, it means exactly one thing, and the numeric fields this replaced
  // took it — rejecting it here would be a step backwards, not a stricter field.
  const scanner = /(\d+(?:\.\d*)?|\.\d+)\s*([a-z]*)\s*/y;
  let minutes = 0;
  let sawHours = false;
  let sawAnything = false;
  let index = 0;

  while (index < normalized.length) {
    scanner.lastIndex = index;
    const token = scanner.exec(normalized);
    if (!token) return null;

    const amount = Number(token[1]);
    if (!Number.isFinite(amount)) return null;
    const unit = token[2];

    if (unit === '') {
      // A bare number trailing an hours token is the minutes half of it, so
      // "1h 30" is 90 minutes rather than 31 hours. On its own it takes the
      // field's default unit.
      minutes += sawHours && sawAnything ? amount : amount * (defaultUnit === 'hours' ? 60 : 1);
    } else if (HOUR_UNIT.test(unit)) {
      minutes += amount * 60;
      sawHours = true;
    } else if (MINUTE_UNIT.test(unit)) {
      minutes += amount;
    } else {
      return null;
    }

    sawAnything = true;
    index = scanner.lastIndex;
  }

  if (!sawAnything) return null;
  return Math.round(minutes);
}

/**
 * The same reading, in hours — the unit `estimatedHours` and friends store.
 *
 * Rounded, because thirds of an hour do not survive the trip otherwise: 20
 * minutes is 0.333333333333333315 hours, which is what would be sent, stored
 * and shown back in a costing figure. Six places is far finer than any duration
 * anyone types and still renders back to the exact minute.
 */
export function parseDurationToHours(
  raw: string,
  options: ParseDurationOptions = {},
): number | null {
  const minutes = parseDurationToMinutes(raw, options);
  if (minutes == null) return null;
  return Math.round((minutes / 60) * 1e6) / 1e6;
}

/**
 * Render minutes back as "1h 30m". Hours lead because that is the unit being
 * typed; a sub-hour duration shows only minutes so "30m" round-trips exactly.
 */
export function formatDurationFromMinutes(totalMinutes: number | null | undefined): string {
  const normalized = Math.max(0, Math.round(totalMinutes || 0));
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/** The same rendering for state held in hours. */
export function formatDurationFromHours(totalHours: number | null | undefined): string {
  return formatDurationFromMinutes(Math.round((totalHours || 0) * 60));
}

/** The hint every labor field shows, so they cannot describe the rules differently. */
export const DURATION_PLACEHOLDER = 'e.g. 1h 30m';
export const DURATION_HINT = 'Hours by default — 2 is 2h. Also accepts 30m, 1h 30m, or 1:30.';
